<template>
  <div class="p-6">
    <h1 class="text-3xl font-bold text-gray-900 dark:text-gray-50 mb-6 flex items-center">
      <button
        @click="$router.push('/wsorganizations')"
        class="mr-4 bg-blue-600 text-white px-4 py-1.5 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition duration-200 text-sm font-medium flex items-center"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          class="h-4 w-4 mr-1"
          viewBox="0 0 20 20"
          fill="currentColor"
        >
          <path
            fill-rule="evenodd"
            d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z"
            clip-rule="evenodd"
          />
        </svg>
        Back to Organisations
      </button>
      {{ isNewOrg ? "Add Organisation" : "Edit Organisation" }}
      <button
          type="button"
          class="hover:bg-transparent bg-transparent ml-2"
          @click="startTutorial"
      >
        <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-circle-question-mark-icon lucide-circle-question-mark dark:text-gray-50 text-gray-900"><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><path d="M12 17h.01"/></svg>
      </button>
    </h1>

    <div v-if="loading" class="text-center py-4">
      <p class="text-gray-600">Loading...</p>
    </div>

    <div
      v-else-if="error"
      class="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative"
      role="alert"
    >
      <strong class="font-bold">Error!</strong>
      <span class="block sm:inline">{{ error }}</span>
    </div>

    <form v-else @submit.prevent="saveOrganization" class="space-y-6 p-6 glass-card">
      <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label for="name" class="block text-sm font-medium text-gray-700 dark:text-white"
            >Name</label
          >
          <input
            type="text"
            id="name"
            v-model="org.name"
            placeholder="Workspace"
            :class="[ 'w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2', fieldErrors['name']
                  ? 'ring-2 ring-red-500  focus:ring-red-500'
                  : 'focus:ring-blue-500'
            ]"
          />
          <FieldError :message="fieldErrors['name']"/>
        </div>

        <div>
          <label
            for="org_key"
            class="block text-sm font-medium text-gray-700 dark:text-white"
            >Organisation Key</label
          >
          <input
            type="text"
            id="org_key"
            placeholder="Workspace-Key"
            v-model="org.org_key"
            :class="[ 'w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2', fieldErrors['org_key']
                  ? 'ring-2 ring-red-500  focus:ring-red-500'
                  : 'focus:ring-blue-500'
            ]"
          />
          <FieldError :message="fieldErrors['org_key']"/>
        </div>

        <div>
          <label
            for="intervals"
            class="block text-sm font-medium text-gray-700 dark:text-white mb-1"
          >
            Interval
          </label>
          
          <div class="flex gap-2">
            <input
              type="number"
              id="intervals"
              @focus="clearFieldError('interval')"
              v-model="agent.intervals"
              required
              min="1"
              :class="[
                'flex-1 border border-gray-300 rounded px-3 py-1 focus:outline-none focus:ring-2',
                fieldErrors['interval']
                  ? 'ring-2 ring-red-500 focus:ring-red-500'
                  : 'focus:ring-blue-500'
              ]"
            />
            
            <GlassSelect
              v-model="agent.intervals_unit"
              :options="[{id:'s',name:'Seconds'},{id:'m',name:'Minutes'}]"
              :error="!!fieldErrors['interval']"
            />
          </div>
          
          <FieldError :message="fieldErrors['interval']"/>
        </div>

        <div>
          <label
            for="metric_batch_size"
            class="block text-sm font-medium text-gray-700 dark:text-white mb-1"
          >
            Metric Batch Size
          </label>
          
          <div class="flex gap-2">
            <input
              type="number"
              id="metric_batch_size"
              @focus="clearFieldError('metric_batch_size')"
              v-model="agent.metric_batch_size"
              required
              min="1"
              :class="[
                'flex-1 border border-gray-300 rounded px-3 py-1 focus:outline-none focus:ring-2',
                fieldErrors['metric_batch_size']
                  ? 'ring-2 ring-red-500 focus:ring-red-500'
                  : 'focus:ring-blue-500'
              ]"
            />
          </div>
          <FieldError :message="fieldErrors['metric_batch_size']"/>
        </div>

        <div>
          <label
            for="metric_buffer_limit"
            class="block text-sm font-medium text-gray-700 dark:text-white mb-1"
          >
            Metric Buffer Limit
          </label>
          
          <div class="flex gap-2">
            <input
              type="number"
              id="metric_buffer_limit"
              @focus="clearFieldError('metric_buffer_limit')"
              v-model="agent.metric_buffer_limit"
              required
              min="1"
              :class="[
                'flex-1 border border-gray-300 rounded px-3 py-1 focus:outline-none focus:ring-2',
                fieldErrors['metric_buffer_limit']
                  ? 'ring-2 ring-red-500 focus:ring-red-500'
                  : 'focus:ring-blue-500'
              ]"
            />
            
          </div>

          <FieldError :message="fieldErrors['metric_buffer_limit']"/>
        </div>

        <div>
          <label
            for="interval"
            class="block text-sm font-medium text-gray-700 dark:text-white mb-1"
          >
            Collection Jitter
          </label>
          
          <div class="flex gap-2">
            <input
              type="number"
              id="collection_jitter"
              @focus="clearFieldError('collection_jitter')"
              v-model="agent.collection_jitter"
              required
              min="0"
              :class="[
                'flex-1 border border-gray-300 rounded px-3 py-1 focus:outline-none focus:ring-2',
                fieldErrors['collection_jitter']
                  ? 'ring-2 ring-red-500 focus:ring-red-500'
                  : 'focus:ring-blue-500'
              ]"
            />
            
            <GlassSelect
              v-model="agent.collection_jitter_unit"
              :options="[{id:'s',name:'Seconds'},{id:'m',name:'Minutes'}]"
              :error="!!fieldErrors['collection_jitter']"
            />
          </div>
          
          <FieldError :message="fieldErrors['collection_jitter']"/>
        </div>

        <div>
          <label
            for="flush_interval"
            class="block text-sm font-medium text-gray-700 dark:text-white mb-1"
          >
            Flush Interval
          </label>
          
          <div class="flex gap-2">
            <input
              type="number"
              id="flush_interval"
               @focus="clearFieldError('flush_interval')"
              v-model="agent.flush_interval"
              required
              min="0"
              :class="[
                'flex-1 border border-gray-300 rounded px-3 py-1 focus:outline-none focus:ring-2',
                fieldErrors['flush_interval']
                  ? 'ring-2 ring-red-500 focus:ring-red-500'
                  : 'focus:ring-blue-500'
              ]"
            />
            
            <GlassSelect
              v-model="agent.flush_interval_unit"
              :options="[{id:'s',name:'Seconds'},{id:'m',name:'Minutes'}]"
              :error="!!fieldErrors['flush_interval']"
            />
          </div>
          
          <FieldError :message="fieldErrors['flush_interval']"/>
        </div>

        <div>
          <label
            for="flush_jitter"
            class="block text-sm font-medium text-gray-700 dark:text-white mb-1"
          >
            Flush Jitter
          </label>
          
          <div class="flex gap-2">
            <input
              type="number"
              id="flush_jitter"
              @focus="clearFieldError('flush_jitter')"
              v-model="agent.flush_jitter"
              required
              min="0"
              :class="[
                'flex-1 border border-gray-300 rounded px-3 py-1 focus:outline-none focus:ring-2',
                fieldErrors['flush_jitter']
                  ? 'ring-2 ring-red-500 focus:ring-red-500'
                  : 'focus:ring-blue-500'
              ]"
            />
            
            <GlassSelect
              v-model="agent.flush_jitter_unit"
              :options="[{id:'s',name:'Seconds'},{id:'m',name:'Minutes'}]"
              :error="!!fieldErrors['flush_jitter']"
            />
          </div>
          
          <FieldError :message="fieldErrors['flush_jitter']"/>
        </div>

        <div>
          <label
            for="precision"
            class="block text-sm font-medium text-gray-700 dark:text-white mb-1"
          >
            Precision
          </label>
          
          <div class="flex gap-2">
            <input
              type="number"
              id="interval"
              @focus="clearFieldError('precision')"
              v-model="agent.precision"
              min="0"
              :class="[
                'flex-1 border border-gray-300 rounded px-3 py-1 focus:outline-none focus:ring-2',
                fieldErrors['precision']
                  ? 'ring-2 ring-red-500 focus:ring-red-500'
                  : 'focus:ring-blue-500'
              ]"
            />
            
            <GlassSelect
              v-model="agent.precision_unit"
              :options="[{id:'s',name:'Seconds'},{id:'ms',name:'Milliseconds'},{id:'ns',name:'Nanoseconds'}]"
              :error="!!fieldErrors['precision']"
            />
          </div>
          <FieldError :message="fieldErrors['precision']"/>
        </div>

        <div>
          <label
            for="hostname"
            class="block text-sm font-medium text-gray-700 dark:text-white"
            >Hostname</label
          >
          <div class="flex gap-2">
            <input
            type="text"
            id="hostname"
            @focus="clearFieldError('hostname')"
            v-model="agent.hostname"
            :class="[ 
              'w-full border border-gray-300 rounded px-3 py-1 focus:outline-none focus:ring-2',
             fieldErrors['hostname']
                  ? 'ring-2 ring-red-500  focus:ring-red-500'
                  : 'focus:ring-blue-500'
            ]"
          />
          </div>
          <FieldError :message="fieldErrors['hostname']"/>
        </div>


        <div class="flex flex-col">
          <label for="round_interval" class="mb-1 font-medium text-gray-700 dark:text-gray-50">
            <div class="relative group flex space-x-3">
              <span>Round Interval</span>
            </div>
          </label>
          <div class="relative inline-block w-12 mr-2 align-middle select-none transition duration-200 ease-in">
            <input 
              v-model="agent.round_interval"
              id="round_interval"
              type="checkbox"
              class="absolute block w-6 h-6 rounded-full bg-white border-4 appearance-none cursor-pointer disabled:cursor-not-allowed peer checked:right-0 checked:bg-blue-600 transition-all duration-300"
            />
            <label 
              for="round_interval"
              id="round_interval_toggle" 
              class="block overflow-hidden h-6 rounded-full bg-gray-300 peer-checked:bg-blue-500 cursor-pointer"
            ></label>
          </div>
        </div>


        <div class="flex flex-col">
          <label for="omit_hostname" class="mb-1 font-medium text-gray-700 dark:text-gray-50">
            <div class="relative group flex space-x-3">
              <span>Omit Hostname</span>
            </div>
          </label>
          <div class="relative inline-block w-12 mr-2 align-middle select-none transition duration-200 ease-in">
            <input 
              v-model="agent.omit_hostname"

              id="omit_hostname"
              type="checkbox"
              class="absolute block w-6 h-6 rounded-full bg-white border-4 appearance-none cursor-pointer disabled:cursor-not-allowed peer checked:right-0 checked:bg-blue-600 transition-all duration-300"
            />
            <label 
              for="omit_hostname"
              id="omit_hostname_toggle" 
              class="block overflow-hidden h-6 rounded-full bg-gray-300 peer-checked:bg-blue-500 cursor-pointer"
            ></label>
          </div>
        </div>


        <div class="flex flex-col">
          <label for="should_validate_configs" class="mb-1 font-medium text-gray-700 dark:text-gray-50">
            <div class="relative group flex space-x-3">
              <span class="block text-sm font-medium text-gray-700 dark:text-white">Allow Collector Validation</span>
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-info-icon lucide-info"><circle cx="12" cy="12" r="10"/><path d="M12 16v-4"/><path d="M12 8h.01"/></svg>
              <div
                class="absolute bottom-full bg-gray-800 text-white dark:bg-gray-200 dark:text-gray-900 text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition pointer-events-none whitespace-nowrap z-10"
              >
                Toggle collector config validation
              </div>
            </div>
          </label>
          <div class="relative inline-block w-12 mr-2 align-middle select-none transition duration-200 ease-in">
            <input 
              id="should_validate_configs"
              :disabled="isNewOrg? !validationSuccessful : false"
              v-model="org.should_validate_configs"
              type="checkbox"
              class="absolute block w-6 h-6 rounded-full bg-white border-4 appearance-none cursor-pointer disabled:cursor-not-allowed peer checked:right-0 checked:bg-blue-600 transition-all duration-300"
            />
            <label 
              for="should_validate_configs"
              id="should_validate_configs_toggle" 
              class="block overflow-hidden h-6 rounded-full bg-gray-300 peer-checked:bg-blue-500 cursor-pointer"
            ></label>
          </div>
        </div>
      </div>


      <div class="space-y-3">
        <div v-if="isNewOrg && !isValidationComplete" class="bg-red-50 border-l-4 border-red-500 p-3 rounded">
          <div class="flex items-start">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              class="w-5 h-5 text-red-500 flex-shrink-0 mr-2"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              stroke-width="2"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
            <p class="text-sm text-red-700">
              <span class="font-semibold">Validation Required:</span> Test the collector configuration validator before creating the organisation.
            </p>
          </div>
        </div>

        <button
          type="button"
          @click="validateCollectorConfigFile"
          :disabled="isValidatingConfig"
          :class="[
            'inline-flex items-center justify-center px-4 py-2 border rounded-md text-sm font-medium transition-all duration-200',
            'focus:outline-none focus:ring-2 focus:ring-offset-2',
            isValidatingConfig
              ? 'bg-gray-400 text-gray-200 cursor-not-allowed border-gray-400'
              : validationSuccessful
              ? 'bg-green-600 hover:bg-green-700 focus:ring-green-500 border-transparent text-white'
              : validationFailed
              ? 'bg-red-600 hover:bg-red-700 focus:ring-red-500 border-transparent text-white'
              : 'bg-indigo-600 hover:bg-indigo-700 focus:ring-indigo-500 border-transparent text-white'
          ]"
        >
          <!-- Loading State -->
          <template v-if="isValidatingConfig">
            <svg
              class="animate-spin h-4 w-4 mr-2"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                class="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                stroke-width="4"
              />
              <path
                class="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 2.21.896 4.21 2.342 5.658l1.658-1.367z"
              />
            </svg>
            Testing...
          </template>

          <!-- Success State -->
          <template v-else-if="validationSuccessful">
            <svg class="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
            </svg>
            Validation Successful
          </template>

          <!-- Error State -->
          <template v-else-if="validationFailed">
            <svg class="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
            Validation Failed - Retry
          </template>

          <!-- Default State -->
          <template v-else>
            Test Validator
          </template>
        </button>
      </div>
      

      <div class="flex justify-end space-x-3">
        <button
          type="button"
          @click="$router.push({ name: 'WorkSpaceOrganizations' })"
          class="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
        >
          Cancel
        </button>
        <button 
          v-if="(canChangeOrg || user?.is_staff) || (isNewOrg && (canAddOrg || user?.is_staff))"
          type="submit"
          :disabled="isNewOrg ? !isValidationComplete : false"
          :class="[
            'px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium',
            'focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500',
            isNewOrg && !isValidationComplete
              ? 'bg-gray-400 text-gray-200 cursor-not-allowed'
              : 'text-white bg-indigo-600 hover:bg-indigo-700'
          ]"
        >
          {{ isNewOrg ? "Create Organisation" : "Save Changes" }}
        </button>
      </div>
    </form>
  </div>
</template>

<script>
import { createwsOrg, updatewsOrg } from "@/services/wsOrgService";
import { validateConfig } from "@/services/configService";
import { getOrgbyID } from "@/services/wsOrgService";
import { useStore } from "@/store";
import FieldError from "@/components/FieldError.vue";
import GlassSelect from "@/components/GlassSelect.vue";
import { 
  getAgentConfigs, 
  createAgentConfig, 
  deleteAgentConfig, 
  updateAgentConfig, 
  getAgentConfigsByOrg 
} from "@/services/collectorAgentService";
import { useTutorial } from "@/composables/tutorial";

export default {
  name: "WorkSpaceOrganizationDetails",
  setup() {
    const { start } = useTutorial();
    return { _start: start };
  },
  components: {
    FieldError,
    GlassSelect
  },
  data() {
    return {
      isValidatingConfig: false,
      validationSuccessful: false,
      validationFailed: false,
      validationMessage: "",
      isValidationComplete: false,
      org: {
        name: "",
        org_key: "",
        should_validate_configs: false
      },
      loading: false,
      error: null,
      isNewOrg: false,
      serviceProviders: [],
      fieldErrors: {},
      agent: {
        id: null,
        intervals: 1,
        intervals_unit: 'm',
        round_interval: true,
        collection_jitter: 5,
        collection_jitter_unit: 's',
        flush_interval: 1,
        flush_interval_unit: 'm',
        flush_jitter: 0,
        flush_jitter_unit: 's',
        metric_batch_size: 1000,
        metric_buffer_limit: 10000,
        precision: 1,
        precision_unit: 'ms',
        hostname: "",
        omit_hostname: false
      },
      canAddOrg: false,
      canChangeOrg: false,
      steps: [
        {
          element: '#name',
          action: 'type',
          value: 'Tutorial Organization',
          message: 'Enter the organization name.'
        },
        {
          element: '#org_key',
          action: 'type',
          value: 'TUTORIAL-ORG-KEY',
          message: 'Enter a unique organization key for identification.'
        },
        {
          element: '#intervals',
          action: 'type',
          value: '30',
          message: 'Set the collection interval - how often metrics are gathered.'
        },
        {
          element: '#metric_batch_size',
          action: 'type',
          value: '100',
          message: 'Set how many metrics are batched together before sending.'
        },
        {
          element: '#metric_buffer_limit',
          action: 'type',
          value: '1000',
          message: 'Set the maximum number of metrics to buffer in memory.'
        },
        {
          element: '#collection_jitter',
          action: 'type',
          value: '5',
          message: 'Add random jitter to collection timing to prevent synchronized spikes.'
        },
        {
          element: '#flush_interval',
          action: 'type',
          value: '10',
          message: 'Set how often buffered metrics are flushed to the server.'
        },
        {
          element: '#flush_jitter',
          action: 'type',
          value: '2',
          message: 'Add jitter to flush timing for better load distribution.'
        },
        {
          element: '#interval',
          action: 'type',
          value: '1',
          message: 'Set the precision for timestamp rounding.'
        },
        {
          element: '#hostname',
          action: 'type',
          value: 'tutorial-host',
          message: 'Enter the hostname for this agent.'
        },
        {
          element: '#round_interval_toggle',
          action: 'checkbox',
          value: null,
          message: 'Enable interval rounding to align collection times.'
        },
        {
          element: '#omit_hostname_toggle',
          action: 'checkbox',
          value: null,
          message: 'Toggle whether to include hostname in metric tags.'
        },
        {
          element: '#should_validate_configs_toggle',
          action: 'checkbox',
          value: null,
          message: 'Enable validation of collector configurations before deployment.'
        },
        {
          element: 'button[type="submit"]',
          action: 'display',
          value: null,
          message: 'Submit to create the organization configuration.'
        }
      ],
    };
  },
  computed: {
    user() {
      return useStore.getUser();
    }, 
    isStaff(){
       return useStore.isStaff();
    },
    formattedOrg() {
      return {
        ...this.org
      };
    },  
  },
  watch: {
    user: {
      handler(user) {
        if (user) {
          this.checkPermissions();
        }
      },
      immediate: true 
    },
    "org.is_service_provider"(newValue) {
      if (newValue) { 
        this.org.service_provider = null;
      }
    },
  },
  async created() {
    const orgId = this.$route.params.id;
    if (orgId === "new") {
      this.isNewOrg = true;
    } else {
      await this.fetchOrganization(orgId);
      await this.fetchCollectorAgent(orgId);
    }
  },
  methods: {

    async checkPermissions() {
      if (this.user) {
        this.canChangeOrg = await useStore.hasPermission("Can change organisation");
        this.canAddOrg = await useStore.hasPermission("Can add organisation");
      }
    },

    convertToSeconds(value, unit){
      const conversions = {'s': 1, 'm': 60, 'h': 3600, 'd': 86400};
      return value * (conversions[unit] || 1);
    },

    validateCollectorData(data){
      this.fieldErrors = {};

      const collectionJitterSeconds = this.convertToSeconds(data.collection_jitter, data.collection_jitter_unit);
      if (collectionJitterSeconds > 30){
        this.fieldErrors['collection_jitter'] = `Collection jitter cannot be greater than 30s`;
      }

      const flushIntervalSeconds = this.convertToSeconds(data.flush_interval, data.flush_interval_unit);
      const flushJitterSeconds = this.convertToSeconds(data.flush_jitter, data.flush_jitter_unit);
      const intervalSeconds = this.convertToSeconds(data.intervals, data.intervals_unit);
      
      if (flushIntervalSeconds + flushJitterSeconds > intervalSeconds) {
        this.fieldErrors['flush_interval'] = `Combined flush interval and jitter must not exceed interval value (${intervalSeconds}s)`;
        this.fieldErrors['flush_jitter'] = `Combined flush interval and jitter must not exceed interval value (${intervalSeconds}s)`;
        this.fieldErrors['interval'] = `Combined flush interval and jitter exceed the set interval`;
      }

      if (data.metric_batch_size > 4000){
        this.fieldErrors['metric_batch_size'] = `Metric batch size cannot be greater than 4000`;
      }

      if (data.metric_buffer_limit > 86000){
        this.fieldErrors['metric_buffer_limit'] = `Metric buffer limit cannot be greater than 86000`;
      }
    },

    clearFieldError(fieldName){
      if (this.fieldErrors[fieldName]) {
        delete this.fieldErrors[fieldName];
      }
    },

    errorValidation(error){
      this.fieldErrors = {};
        const errData = error?.response?.data;
        if (errData) {
          for (const key in errData) {
            const value = errData[key];

            if (Array.isArray(value)) {
              this.fieldErrors[key] = value[0];
            } 
            else if (typeof value === 'object' && value !== null) {
              const nestedFirstKey = Object.keys(value)[0];
              const nestedMessages = value[nestedFirstKey];

              if (Array.isArray(nestedMessages)) {
                this.fieldErrors[key] = nestedMessages[0];
              }
            }
          }
        }
    },

    async fetchOrganization(orgId) {
      this.loading = true;
      this.error = null;
      try {
        this.org = await getOrgbyID(orgId);
      } catch (error) {
        console.error("Error fetching organization:", error);
        this.error =
          "An error occurred while fetching organization details. Please try again later.";
      } finally {
        this.loading = false;
      }
    },

    async fetchCollectorAgent(organisationId){
      try {
        const response = await getAgentConfigsByOrg(organisationId);

        if (response && response.length > 0) {
          this.agent = response[0];
        } else {
          this.agent = this.agent
        }
      } catch (error) {
        console.error(error)
      }
    },

    async saveCollectorAgent(organisation){
      try {
        const data = {
          ...this.agent,
          organization: organisation.id
        }

        const isNewConfig = !data.id || data.id === null;

        if (isNewConfig){
          await createAgentConfig(data);
        } else {
          await updateAgentConfig(data.id, data);
        }

      } catch (error) {
        console.error(error)
        this.errorValidation(error);
        throw error;
      }
    },

    async saveOrganization() {
      this.loading = true;
      this.error = null;
      
      try {
        const orgData = this.formattedOrg;
        
        this.validateCollectorData(this.agent)
        if (Object.keys(this.fieldErrors).length > 0) return;

        let response;
        if (this.isNewOrg) {
          response = await createwsOrg(orgData);
        } else {
          response = await updatewsOrg(this.org.id, orgData);
        }

        if (response){
          await this.saveCollectorAgent(response);
        }

        this.$router.push({ name: "RateLimitsView" });
      } catch (error) {
        this.errorValidation(error);
        console.error("Error saving organization:", error);
      } finally {
        this.loading = false;
      }
    },
    async validateCollectorConfigFile() {
      try {
        this.isValidatingConfig = true;

        const fileContent = "[agent]\ninterval = \"10s\"\nround_interval = true\nmetric_batch_size = 1000\n\n[[inputs.cpu]]\n  percpu = true\n  totalcpu = true\n  collect_cpu_time = false\n\n[[inputs.mem]]\n\n[[outputs.file]]\n  files = [\"stdout\"]\n  data_format = \"influx\"";
        const file = new File([fileContent], "collector_example.conf", {
          type: "text/plain",
        });
        
        const response = await validateConfig(file);

        this.validationSuccessful = response.status === "ok";
        this.validationFailed = !this.validationSuccessful;
        this.validationMessage = response.message;
        this.org.should_validate_configs = this.validationSuccessful;

      } catch (error) {
        this.validationSuccessful = false;
        this.validationFailed = true;
        this.validationMessage = error?.response?.data?.message || "Validation failed";
        this.org.should_validate_configs = false;
      } finally {
        this.isValidatingConfig = false;
        this.isValidationComplete = true;
      }
    },
    async startTutorial(){
      const originalOrg = JSON.parse(JSON.stringify(this.org));
      const originalAgent = JSON.parse(JSON.stringify(this.agent));
      
      await this._start(this.steps, () => {
        this.org = originalOrg;
        this.agent = originalAgent;
      });
    }
  },
};
</script>